// The setup steps this screen knows (UC-59, #774): Till settings (T26) shows
// one row for each and the onboarding (T27) one page.
//
// The till's order store says which steps the till offers, in their order,
// and which have passed (GET /store/till-setup); the rules are the core's
// TillSetup. This list only says how the screen shows a step: its name, its
// state line, whether the screen can tell yet, whether it is set, and the
// view Open shows in Till settings. A step the store lists that this list
// does not hold shows nowhere and stays pending in the store. A later step
// joins with its entry here and its page in SetUpTill.svelte; the store needs
// no change. The sharing step (T28, #775) is the store's first on a Windows
// till; an Android or a Linux store never lists it. The cash-point step
// (T32, #792) follows printers on every till.

import type { components } from '../api/store';
import { message, type Language, type MessageKey } from '../i18n/messages';
import { countLine } from './storeState.svelte';
import type { CashPointBody } from './cashPoint.svelte';
import type { MeshState } from './mesh.svelte';

/** What the store answers about the till's setup. */
export type Setup = components['schemas']['StoreTillSetupBody'];

/** What a step's state line and its set-or-not read: the till's printers. */
export interface StepContext {
	/** The store answered the printers once, so their count is known. */
	printersRead: boolean;
	printerCount: number;
	/** The purposes whose printer did not answer a print (T25's "Not answering"). */
	notAnswering: readonly string[];
	/** The mesh's state as last read; null before any answer. */
	meshState: MeshState | null;
	/** Where the till's cash goes as the store last said; null before any answer. */
	cashPoint: CashPointBody['cashPoint'] | null;
	/** The last read of where the till's cash goes failed. */
	cashPointFailed: boolean;
	/** The step's pick can stand: the kept cash point, or one Confirm can send (src/till/cashPoint.svelte.ts). */
	cashPointReady: boolean;
}

/** The steps this screen knows, by the store's id. */
export const setupSteps = {
	sharing: {
		id: 'sharing',
		name: 'sharingTitle' as MessageKey,
		state: (language: Language, context: StepContext) =>
			context.meshState === 'on'
				? message(language, 'sharingRowOn')
				: context.meshState === 'off'
					? message(language, 'sharingRowOff')
					: '',
		// "Needs Windows' yes" reads as the row's flag chip, in place of a line.
		flag: (context: StepContext) => context.meshState === 'needs_firewall',
		flagText: 'sharingRowNeedsYes' as MessageKey,
		// Its page reads the mesh itself as it shows, with the compass after
		// 300 ms and a failure with Retry, so the onboarding never waits for it.
		ready: () => true,
		// Set once the mesh is on; Skip passes it while it is not.
		isSet: (context: StepContext) => context.meshState === 'on',
		// Open in Till settings shows T28 as a Sheet over it.
		open: 'sharing',
	},
	printers: {
		id: 'printers',
		name: 'printersTitle' as MessageKey,
		state: (language: Language, context: StepContext) =>
			context.printersRead ? printerState(language, context.printerCount) : '',
		flag: (context: StepContext) => context.notAnswering.length > 0,
		flagText: 'printerNotAnswering' as MessageKey,
		// Before the printers are read the step can say nothing, so the
		// onboarding waits for them rather than offer Skip on a till that has one.
		ready: (context: StepContext) => context.printersRead,
		// Set once the till has a printer; Skip passes it with none.
		isSet: (context: StepContext) => context.printersRead && context.printerCount > 0,
		// Open in Till settings, and Set up printers in the onboarding, show T25.
		open: 'printers',
	},
	cash_point: {
		id: 'cash_point',
		name: 'cashPointTitle' as MessageKey,
		state: (language: Language, context: StepContext) =>
			context.cashPoint === null
				? ''
				: context.cashPoint.self
					? message(language, 'cashPointThisTill')
					: context.cashPoint.name,
		// "Removed" reads as the row's flag chip when the owner revoked the cash point.
		flag: (context: StepContext) => context.cashPoint?.removed ?? false,
		flagText: 'cashPointRemoved' as MessageKey,
		// Its page reads the cash point and says it waits, so the onboarding
		// never waits for it.
		ready: () => true,
		// Set while the pick can stand; Skip shows while another till still
		// waits for its link test, the kept one was removed, or the read failed.
		isSet: (context: StepContext) =>
			context.cashPoint !== null && !context.cashPointFailed && context.cashPointReady,
		// Open in Till settings shows T32 as a Sheet over it.
		open: 'cash_point',
	},
} as const;

/**
 * The store's steps this screen knows, in the store's order, each with how it
 * passed. An id it does not know is dropped, an Object.prototype member's
 * name included.
 */
export function knownSteps(body: Setup | null) {
	return (body?.steps ?? []).flatMap((step) =>
		Object.hasOwn(setupSteps, step.id)
			? [{ ...setupSteps[step.id as keyof typeof setupSteps], passed: step.passed }]
			: [],
	);
}

/** The known step the onboarding opens at: the first that has not passed, or -1. */
export function onboardingIndex(body: Setup | null): number {
	return knownSteps(body).findIndex((step) => step.passed === undefined);
}

/** Whether the onboarding opens: it is pending and a known step has not passed. */
export function shouldOpenSetup(body: Setup | null): boolean {
	return body?.onboarding === 'pending' && onboardingIndex(body) !== -1;
}

/** The Sheet's one figure, "1 / 2", only while more than one step shows. */
export function setupFigure(index: number, total: number): string | undefined {
	return total > 1 ? `${index + 1} / ${total}` : undefined;
}

/**
 * Whether the step at `index` is the onboarding's last to pass: no known step
 * after it waits. A step an older screen left pending, such as sharing on a
 * Windows till whose printers passed, is the last though another follows it.
 */
export function isLastOpenStep(body: Setup | null, index: number): boolean {
	return !knownSteps(body).some((step, at) => at > index && step.passed === undefined);
}

/** The first known step after `index` that has not passed, or -1. */
export function nextOpenIndex(body: Setup | null, index: number): number {
	return knownSteps(body).findIndex((step, at) => at > index && step.passed === undefined);
}

/**
 * The onboarding's bottom row on one step: Skip only while the step is not
 * set; Next before the last step to pass and Confirm with its check on it, on
 * only once the step is set. Skip passes the step skipped, the primary done.
 */
export function stepActions(last: boolean, isSet: boolean) {
	return {
		skip: !isSet,
		primary: (last ? 'confirm' : 'setupNext') as MessageKey,
		icon: last ? ('check' as const) : undefined,
		disabled: !isSet,
	};
}

/**
 * The Printers row's state line: how many printers the till has, or none. A
 * shop has at most two (UC-47), so the many line never shows in practice.
 */
export function printerState(language: Language, count: number): string {
	if (count <= 0) return message(language, 'setupPrintersNone');
	return countLine(language, count, 'setupPrinterOne', 'setupPrintersTwo', 'setupPrintersMany');
}

/** What decides whether the onboarding (T27) shows now. */
export interface SetupShowing {
	isManager: boolean;
	/** The one signed in now. */
	userId: string;
	/** Whose sign-in read the setup in hand; null while none did. */
	readFor: string | null;
	/** Who closed the onboarding in this session; null while nobody did. */
	closedBy: string | null;
	/** The read in hand is on its way, failed or was refused. */
	unread: boolean;
	body: Setup | null;
	context: StepContext;
	/** The step the onboarding stands at. */
	index: number;
	view: string;
	locked: boolean;
	/** Any other Sheet is open over the till. */
	otherSheet: boolean;
}

/**
 * Whether the onboarding shows: for the manager whose sign-in read it, while
 * the store says it is pending at a known step not passed, and its page can
 * say what it holds; over Sell only, with no other Sheet open and the till
 * not locked. A Close holds for the session that closed it.
 */
export function setupShows(s: SetupShowing): boolean {
	if (!s.isManager || s.readFor !== s.userId || s.closedBy === s.userId || s.unread) return false;
	if (!shouldOpenSetup(s.body)) return false;
	const step = knownSteps(s.body)[s.index];
	if (step === undefined || !step.ready(s.context)) return false;
	return s.view === 'sell' && !s.locked && !s.otherSheet;
}
